

import React, { useState, useEffect } from 'react';
import { 
    generateApiKey, 
    listMyApiKeys, 
    listAllApiKeys, 
    revokeApiKey, 
    rotateApiKey,
    fetchUsers 
} from '../services/api';

export default function APIKeyManager({ isAdmin = false }) {
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
    const [copiedKeyId, setCopiedKeyId] = useState(null);
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [activeTab, setActiveTab] = useState('active'); // 'active' or 'revoked'
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(''); // For filtering
    
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        permissions: 'read',
        expires_in_days: '',
        user_id: null
    });

    useEffect(() => {
        loadKeys();
        if (isAdmin) {
            loadUsers();
        }
    }, [isAdmin]);

    useEffect(() => {
        // Load users when modal opens (for admins only)
        if (showCreateModal && isAdmin && users.length === 0) {
            loadUsers();
        }
    }, [showCreateModal, isAdmin]);

    const loadKeys = async () => {
        try {
            setLoading(true);
            const response = isAdmin 
                ? await listAllApiKeys()
                : await listMyApiKeys();
            setKeys(response.data);
        } catch (err) {
            console.error('Error loading keys:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadUsers = async () => {
        try {
            setLoadingUsers(true);
            const response = await fetchUsers();
            setUsers(response.data);
        } catch (err) {
            console.error('Error loading users:', err);
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleCreateKey = async () => {
        try {
            const keyData = {
                ...formData,
                expires_in_days: formData.expires_in_days ? parseInt(formData.expires_in_days) : null,
                ...(isAdmin && formData.user_id ? { user_id: formData.user_id } : {})
            };
            
            const response = await generateApiKey(keyData);
            setNewlyCreatedKey(response.data.key);
            await loadKeys();
        } catch (err) {
            console.error('Error creating key:', err);
            alert('Failed to create API key: ' + (err.response?.data?.detail || err.message));
        }
    };

    const handleRevokeKey = async (keyId) => {
        if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
            return;
        }
        
        try {
            await revokeApiKey(keyId);
            await loadKeys(); // Reload to get updated list
        } catch (err) {
            console.error('Error revoking key:', err);
            alert('Failed to revoke API key');
        }
    };

    const handleRotateKey = async (keyId) => {
        if (!window.confirm('This will invalidate the current key and generate a new one. Continue?')) {
            return;
        }
        
        try {
            const response = await rotateApiKey(keyId);
            setNewlyCreatedKey(response.data.key);
            setShowCreateModal(true); // Show modal with new key
            await loadKeys();
        } catch (err) {
            console.error('Error rotating key:', err);
            alert('Failed to rotate API key');
        }
    };

    const copyToClipboard = async (text, keyId = null) => {
        try {
            await navigator.clipboard.writeText(text);
            if (keyId) {
                setCopiedKeyId(keyId);
                setTimeout(() => setCopiedKeyId(null), 2000);
            }
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleDateString();
    };

    const getPermissionBadgeColor = (permission) => {
        switch (permission) {
            case 'admin': return 'bg-red-100 text-red-800';
            case 'write': return 'bg-yellow-100 text-yellow-800';
            case 'read': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    // Filter and sort keys
    const filteredKeys = keys
        .filter(key => {
            // Filter by active/revoked status
            if (activeTab === 'active' && !key.is_active) return false;
            if (activeTab === 'revoked' && key.is_active) return false;
            
            // Filter by search term
            if (searchTerm) {
                const search = searchTerm.toLowerCase();
                if (!key.name.toLowerCase().includes(search) && 
                    !(key.description && key.description.toLowerCase().includes(search)) &&
                    !(key.username && key.username.toLowerCase().includes(search))) {
                    return false;
                }
            }
            
            // Filter by selected user (admin only)
            if (isAdmin && selectedUser && key.user_id !== parseInt(selectedUser)) {
                return false;
            }
            
            return true;
        })
        .sort((a, b) => {
            // Sort by creation date, newest first
            return new Date(b.created_at) - new Date(a.created_at);
        });

    const activeCount = keys.filter(k => k.is_active).length;
    const revokedCount = keys.filter(k => !k.is_active).length;

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="text-lg font-medium text-gray-900">
                            API Keys {isAdmin && '(All Users)'}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Manage API keys for accessing the internal system
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            setShowCreateModal(true);
                            setNewlyCreatedKey(null);
                            setFormData({
                                name: '',
                                description: '',
                                permissions: 'read',
                                expires_in_days: '',
                                user_id: null
                            });
                        }}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Generate New Key
                    </button>
                </div>
            </div>

            {/* Filters and Tabs */}
            <div className="bg-white shadow rounded-lg p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                    {/* Tabs */}
                    <div className="flex space-x-1">
                        <button
                            onClick={() => setActiveTab('active')}
                            className={`px-4 py-2 rounded-md ${
                                activeTab === 'active'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Active ({activeCount})
                        </button>
                        <button
                            onClick={() => setActiveTab('revoked')}
                            className={`px-4 py-2 rounded-md ${
                                activeTab === 'revoked'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Revoked ({revokedCount})
                        </button>
                    </div>

                    {/* Search and Filter */}
                    <div className="flex flex-1 gap-2">
                        <input
                            type="text"
                            placeholder="Search by name, description, or user..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                        
                        {isAdmin && (
                            <select
                                value={selectedUser}
                                onChange={(e) => setSelectedUser(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">All Users</option>
                                {users.map(user => (
                                    <option key={user.id} value={user.id}>
                                        {user.username} ({user.role})
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                </div>
            </div>

            {/* API Keys List */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Key Prefix</th>
                            {isAdmin && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>}
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Permissions</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Used</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredKeys.length === 0 ? (
                            <tr>
                                <td colSpan={isAdmin ? 9 : 8} className="px-6 py-4 text-center text-sm text-gray-500">
                                    {searchTerm || selectedUser 
                                        ? 'No API keys found matching your filters.'
                                        : activeTab === 'active' 
                                            ? 'No active API keys found. Generate one to get started.'
                                            : 'No revoked API keys found.'}
                                </td>
                            </tr>
                        ) : (
                            filteredKeys.map((key) => (
                                <tr key={key.id} className={!key.is_active ? 'bg-gray-50' : ''}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">{key.name}</div>
                                            {key.description && (
                                                <div className="text-sm text-gray-500">{key.description}</div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                                                {key.prefix}...
                                            </code>
                                            <button
                                                onClick={() => copyToClipboard(key.prefix, key.id)}
                                                className="ml-2 text-gray-400 hover:text-gray-600"
                                            >
                                                {copiedKeyId === key.id ? (
                                                    <svg className="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                ) : (
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    </td>
                                    {isAdmin && (
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {key.username}
                                        </td>
                                    )}
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPermissionBadgeColor(key.permissions)}`}>
                                            {key.permissions}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatDate(key.created_at)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatDate(key.last_used)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatDate(key.expires_at)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                            key.is_active 
                                                ? 'bg-green-100 text-green-800' 
                                                : 'bg-red-100 text-red-800'
                                        }`}>
                                            {key.is_active ? 'Active' : 'Revoked'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {key.is_active && (
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => handleRotateKey(key.id)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                    title="Rotate key"
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleRevokeKey(key.id)}
                                                    className="text-red-600 hover:text-red-900"
                                                    title="Revoke key"
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create/Display Key Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => !newlyCreatedKey && setShowCreateModal(false)} />
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
                            <div className="p-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    {newlyCreatedKey ? 'Your New API Key' : 'Generate API Key'}
                                </h3>

                                {newlyCreatedKey ? (
                                    <div className="space-y-4">
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                                            <div className="flex">
                                                <svg className="h-5 w-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                                <div className="text-sm text-yellow-800">
                                                    <p className="font-medium">Important!</p>
                                                    <p>Copy this key now. You won't be able to see it again.</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-gray-50 rounded-md p-4">
                                            <code className="text-sm font-mono break-all">{newlyCreatedKey}</code>
                                        </div>

                                        <button
                                            onClick={() => copyToClipboard(newlyCreatedKey)}
                                            className="w-full inline-flex justify-center items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                        >
                                            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                            Copy to Clipboard
                                        </button>

                                        <button
                                            onClick={() => {
                                                setShowCreateModal(false);
                                                setNewlyCreatedKey(null);
                                            }}
                                            className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                                        >
                                            Done
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {/* User Selection for Admins */}
                                        {isAdmin && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Create Key For User
                                                </label>
                                                {loadingUsers ? (
                                                    <div className="text-sm text-gray-500">Loading users...</div>
                                                ) : (
                                                    <select
                                                        value={formData.user_id || ''}
                                                        onChange={(e) => setFormData({ ...formData, user_id: e.target.value ? parseInt(e.target.value) : null })}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="">Current User (Yourself)</option>
                                                        {users.map(user => (
                                                            <option key={user.id} value={user.id}>
                                                                {user.username} ({user.role})
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        )}

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Name *
                                            </label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="e.g., Ecosystem Integration"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Description
                                            </label>
                                            <textarea
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                rows={2}
                                                placeholder="Optional description..."
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Permissions
                                            </label>
                                            <select
                                                value={formData.permissions}
                                                onChange={(e) => setFormData({ ...formData, permissions: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="read">Read Only</option>
                                                <option value="write">Read & Write</option>
                                                {isAdmin && <option value="admin">Admin</option>}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Expires In (days)
                                            </label>
                                            <input
                                                type="number"
                                                value={formData.expires_in_days}
                                                onChange={(e) => setFormData({ ...formData, expires_in_days: e.target.value })}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Leave empty for no expiration"
                                                min="1"
                                            />
                                        </div>

                                        <div className="flex gap-3">
                                            <button
                                                onClick={() => {
                                                    if (formData.name) {
                                                        handleCreateKey();
                                                    }
                                                }}
                                                disabled={!formData.name}
                                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                                            >
                                                Generate Key
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setShowCreateModal(false)}
                                                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}